import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import APIserver from '../../../../api/axiosConfig';
import AlertsFacturas from '../../facturas/Utils/AlertsFacturas';

const CrearModalDev = ({ show, handleClose, numeroFactura }) => {
    const [formData, setFormData] = useState({
        numeroFactura: numeroFactura || '', // Inicializa con el valor recibido
        cliente: '',
        detalle: '',
        fecha: new Date().toISOString().split('T')[0], // Establecer fecha actual como valor por defecto
        banco: null,
        monto: ''
    });
    const [bancos, setBancos] = useState([]);
    const [selectedBanco, setSelectedBanco] = useState(null);
    const [errors, setErrors] = useState({});
    const [montoMaximo, setMontoMaximo] = useState(0);

    // Actualizar el número de factura cuando cambie el prop numeroFactura
    useEffect(() => {
        if (numeroFactura) {
            setFormData((prevData) => ({
                ...prevData,
                numeroFactura
            }));
        }
    }, [numeroFactura]);

    // Fetch de bancos
    const fetchBancos = async () => {
        try {
            const response = await APIserver.get(`/api/pagosrecibidos/mostrar/bancos`);
            if (response.data && Array.isArray(response.data.bancos)) {
                setBancos(response.data.bancos.map(banco => ({ value: banco.idBanco, label: banco.nomBanco })));
            } else {
                setBancos([]);
            }
        } catch (error) {
            console.error('Error al obtener los bancos:', error);
            setBancos([]);
        }
    };

    // Obtener el cliente basado en el número de factura
    const fetchClienteByFactura = async (numeroFactura) => {
        if (numeroFactura.trim() !== '') {
            try {
                const response = await APIserver.get(`/api/pagosrecibidos/nombreCliente/${numeroFactura}`);
                const { nombreCliente, totalFacV, pagadoFacV } = response.data;

                if (totalFacV === undefined || pagadoFacV === undefined) {
                    console.error('Datos de factura incompletos:', response.data);
                    AlertsFacturas.showErrorMessage('Datos de factura incompletos.');
                    return;
                }

                // Actualizar datos del formulario
                setFormData(prevFormData => ({
                    ...prevFormData,
                    cliente: nombreCliente
                }));
                setMontoMaximo(totalFacV - pagadoFacV);
            } catch (error) {
                console.error('Error al obtener los datos del cliente:', error);
                AlertsFacturas.showErrorMessage('Factura no encontrada.');
            }
        }
    };

    // Ejecuta la búsqueda de bancos al montar el componente
    useEffect(() => {
        fetchBancos();  // Cargar bancos al cargar el componente
    }, []);

    // Ejecutar la búsqueda de cliente cada vez que el número de factura cambie
    useEffect(() => {
        if (formData.numeroFactura) {
            fetchClienteByFactura(formData.numeroFactura);
        }
    }, [formData.numeroFactura]);

    // Manejo del cambio en el campo de texto
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Manejo de la selección del banco
    const handleBancoChange = (selectedOption) => {
        setSelectedBanco(selectedOption);
    };

    // Validación del formulario
    const validateForm = () => {
        const newErrors = {};
        if (!formData.numeroFactura.trim()) newErrors.numeroFactura = 'Este campo es obligatorio';
        if (!formData.cliente.trim()) newErrors.cliente = 'Este campo es obligatorio';
        if (!formData.detalle.trim()) newErrors.detalle = 'Este campo es obligatorio';
        if (!formData.fecha) newErrors.fecha = 'Este campo es obligatorio';
        if (!selectedBanco) newErrors.banco = 'Seleccione un banco';
        if (!formData.monto || parseFloat(formData.monto) <= 0) newErrors.monto = 'Ingrese un monto válido';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Enviar los datos al backend
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return; // Si hay errores, no continuar

        try {
            // Cambié 'numeroFactura' a 'idFacV' y 'banco' a 'idBanco'
            const response = await APIserver.post('/api/devoluciones/guardar', {
                numFacV: formData.numeroFactura,  // Cambié el nombre del campo
                detalle: formData.detalle,
                monto: formData.monto,
                idBanco: selectedBanco.value,    // Cambié el nombre del campo
                fecha: formData.fecha
            });

            // Mostrar alerta de éxito
            AlertsFacturas.showUpdateSuccessMessage('Devolución creada correctamente.');

            // Cerrar el modal
            handleClose();
        } catch (error) {
            console.error('Error al crear la devolución:', error);
            if (error.response) {
                console.error('Detalles del error:', error.response.data);
                AlertsFacturas.showErrorMessage(error.response.data.message || 'Hubo un error al crear la devolución.');
            } else {
                AlertsFacturas.showErrorMessage('Hubo un error al crear la devolución.');
            }
        }
    };

    return (
        <Modal size="lg" show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Crear Devolución</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col>
                            <Form.Group controlId="formNumeroFactura" className='m-3 mt-0'>
                                <Form.Label>Número de Factura</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="numeroFactura"
                                    value={formData.numeroFactura}
                                    onChange={handleChange}
                                    required
                                    isInvalid={!!errors.numeroFactura}
                                />
                                {errors.numeroFactura && <div className="invalid-feedback d-block">{errors.numeroFactura}</div>}
                            </Form.Group>

                            <Form.Group controlId="formCliente" className='m-3'>
                                <Form.Label>Cliente</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="cliente"
                                    value={formData.cliente}
                                    onChange={handleChange}
                                    readOnly
                                    isInvalid={!!errors.cliente}
                                />
                                {errors.cliente && <div className="invalid-feedback d-block">{errors.cliente}</div>}
                            </Form.Group>

                            <Form.Group controlId="formDetalle" className='m-3'>
                                <Form.Label>Detalle</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="detalle"
                                    value={formData.detalle}
                                    onChange={handleChange}
                                    required
                                    isInvalid={!!errors.detalle}
                                />
                                {errors.detalle && <div className="invalid-feedback d-block">{errors.detalle}</div>}
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group controlId="formFecha" className='m-3 mt-0'>
                                <Form.Label>Fecha</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="fecha"
                                    value={formData.fecha}
                                    onChange={handleChange}
                                    required
                                    isInvalid={!!errors.fecha}
                                />
                                {errors.fecha && <div className="invalid-feedback d-block">{errors.fecha}</div>}
                            </Form.Group>

                            <Form.Group controlId="formBanco" className='m-3'>
                                <Form.Label>Banco</Form.Label>
                                <Select
                                    placeholder="Seleccione el Banco"
                                    options={bancos}
                                    value={selectedBanco}
                                    onChange={handleBancoChange}
                                    isInvalid={!!errors.banco}
                                />
                                {errors.banco && <div className="invalid-feedback d-block">{errors.banco}</div>}
                            </Form.Group>

                            <Form.Group controlId="formMonto" className='m-3'>
                                <Form.Label>Monto</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="monto"
                                    value={formData.monto}
                                    onChange={handleChange}
                                    required
                                    isInvalid={!!errors.monto}
                                />
                                {errors.monto && <div className="invalid-feedback d-block">{errors.monto}</div>}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Modal.Footer>
                        <Button variant='dark' onClick={handleClose}>
                            Cancelar
                        </Button>
                        <Button variant="danger" type="submit">
                            Guardar
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default CrearModalDev;
